import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Phone } from "lucide-react";
import api from "../lib/api";
import { BlogCard } from "../sections/BlogPreview";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";

export function BlogListPage() {
  const [posts, setPosts] = useState(null);
  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)).catch(() => setPosts([])); }, []);
  return (
    <div className="pt-32 pb-28 bg-white min-h-screen" data-testid="blog-list-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal className="mb-16 max-w-3xl">
          <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#6B1414]">Insights</span>
          <h1 className="mt-4 font-heading text-5xl sm:text-6xl font-black tracking-[-0.03em] text-[#0A0A0A] leading-[0.95]">Marketplace playbooks, written by operators.</h1>
        </Reveal>
        {posts === null ? <p className="font-mono text-xs text-gray-400">LOADING...</p> : posts.length === 0 ? <p className="font-mono text-xs text-gray-400" data-testid="blog-empty">No articles yet.</p> : (
          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {posts.map((p) => <StaggerItem key={p.id}><BlogCard p={p} /></StaggerItem>)}
          </Stagger>
        )}
      </div>
    </div>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    setPost(null); setError(false);
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setError(true));
    window.scrollTo(0, 0);
  }, [slug]);

  if (error) return <div className="pt-40 pb-28 text-center font-mono text-sm" data-testid="blog-not-found">Article not found. <Link to="/blog" className="underline">Back to insights</Link></div>;
  if (!post) return <div className="pt-40 pb-28 text-center font-mono text-xs text-gray-400">LOADING...</div>;

  return (
    <article className="bg-white min-h-screen" data-testid="blog-post-page">
      <div className="relative h-[60vh] min-h-[420px] bg-[#0A0A0A] overflow-hidden">
        {post.cover_image && <img src={post.cover_image} alt={post.title} className="absolute inset-0 w-full h-full object-cover opacity-60 saturate-[0.7]" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 h-full flex flex-col justify-end pb-14 pt-32">
          <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-[#C8956A] mb-6 hover:text-white"><ArrowLeft size={12} /> All insights</Link>
          <div className="flex gap-2 mb-4">{(post.tags || []).map((t) => <span key={t} className="font-mono text-[9px] uppercase tracking-wider bg-[#F5D5B0] text-[#0A0A0A] px-2 py-1 rounded">{t}</span>)}</div>
          <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-[-0.03em] text-white leading-[1.02]" data-testid="blog-post-title">{post.title}</h1>
          <p className="mt-5 font-mono text-[10px] uppercase tracking-wider text-white/60">{post.read_time} min read · {new Date(post.timestamp).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })} · Calium Seller Services</p>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-6 lg:px-8 py-16">
        <p className="font-heading text-xl sm:text-2xl font-bold text-[#0A0A0A] leading-snug border-l-4 border-[#6B1414] pl-6 mb-12">{post.excerpt}</p>
        <div className="prose-calium font-body text-[#0A0A0A]" data-testid="blog-post-content">
          <ReactMarkdown>{post.content}</ReactMarkdown>
        </div>
        <div className="mt-16 bg-[#0A0A0A] text-white rounded-md p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C8956A]">Need this done for you?</p>
            <p className="font-heading text-2xl font-black mt-1">Talk to a Calium consultant.</p>
          </div>
          <a href="tel:+917827304742" className="inline-flex items-center gap-2 rounded-full bg-[#F5D5B0] text-[#0A0A0A] px-6 py-3 font-body font-bold text-sm"><Phone size={14} /> +91 78273 04742</a>
        </div>
      </div>
    </article>
  );
}
